import { merge, Subject, of } from 'rxjs';
import { catchError, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import {
  Component,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  AfterViewInit,
  Input,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

import { SortParameters } from '@lib/api';
import { SnAlarmType, SnWidget, SnAlarm } from '@lib/api/entities';
import { SnAlarmsAPI } from '@lib/api/services';
import { snDetailExpand } from '@lib/common/animations';
import { AlprDetectionService } from '@lib/vehicle-intelligence';

@Component({
  selector: 'fs-alarms-widget',
  templateUrl: './alarms-widget.component.html',
  styleUrls: ['./alarms-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [snDetailExpand],
})
export class AlarmsWidgetComponent implements OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) public paginator: MatPaginator;
  @ViewChild(MatSort) public sort: MatSort;
  @Input() public widget: SnWidget;
  @Input() public viewId: string;

  public data: any;
  public changeTitle = false;
  public types: SnAlarmType[] = [];
  public displayedColumns = ['type', 'message', 'createdAt'];
  public showClosed = new UntypedFormControl({});
  public resultsLength = 0;
  public isLoadingResults = true;

  private readonly _destroy$ = new Subject<void>();
  private readonly _expanded = [];

  constructor(
    private readonly _alarmsService: SnAlarmsAPI,
    private readonly _cd: ChangeDetectorRef,
    private readonly ref: ChangeDetectorRef,
    private readonly _alprDetectionService: AlprDetectionService,
  ) {}

  public ngAfterViewInit(): void {
    let typeAlarmObject = <any>{};
    if (localStorage.getItem('alarmType')) {
      typeAlarmObject = JSON.parse(localStorage.getItem('alarmType'));
      this.filterAndFetchAlarm(typeAlarmObject.name, typeAlarmObject.type);
    } else {
      this.filterAndFetchAlarm(null, null);
    }

    this._alarmsService
      .getAlarmTypeList({})
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        this.types = res.results;
      });

    this.showClosed.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.paginator.pageIndex = 0;
      if (localStorage.getItem('alarmType')) {
        typeAlarmObject = JSON.parse(localStorage.getItem('alarmType'));
        this.filterAndFetchAlarm(typeAlarmObject.name, typeAlarmObject.type);
      } else {
        this.filterAndFetchAlarm(null, null);
      }
    });

    this.sort.sortChange
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => (this.paginator.pageIndex = 0));
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public filterAndFetchAlarm(name: string, type?: string): void {
    this.isLoadingResults = true;
    merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          return this._alarmsService
            .getAlarmList({
              status: this.showClosed.value ? '' : 'Acked',
              ordering: new SortParameters({
                active: this.sort.active,
                direction: this.sort.direction,
              }).convertedActive,
              page: this.paginator.pageIndex + 1,
              pageSize: 5,
              type,
            })
            .pipe(catchError(() => of(null)));
        }),
        map((data) => {
          if (data === null) {
            return [];
          }
          this.resultsLength = data.count;
          return data.results;
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((data) => {
        this.data = data;
        if (!name && !type) {
          localStorage.removeItem('alarmType');
        } else {
          localStorage.setItem('alarmType', JSON.stringify({ name, type }));
        }
        this.isLoadingResults = false;
        this.ref.detectChanges();
      });
  }

  public toggleExpanded(id: string): void {
    if (this.isExpanded(id)) {
      this._expanded.splice(this._expanded.indexOf(id), 1);
    } else {
      this._expanded.push(id);
    }
  }

  public isExpanded(id: string): boolean {
    return this._expanded.indexOf(id) > -1;
  }

  public setUpdateTitle(value: string): void {
    this.widget.name = value;
    this._cd.detectChanges();
  }

  public editTitle(): void {
    this.changeTitle = true;
  }

  public setTitle(value: boolean): void {
    this.changeTitle = value;
    this._cd.detectChanges();
  }

  public isAlprAlarm(alarm: SnAlarm): boolean {
    return this._alprDetectionService.isAlprAlarm(alarm);
  }
}
